import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  FormikErrorMessage,
  useFormikFieldError,
} from "@/components/FormikErrorMessage";
import { cn } from "@/lib/utils";
import type { FormikProps } from "formik";
import { getIn } from "formik";
import type { SilaeVariable } from "@/types/silae";
import type { SilaeFormData } from "@/types/silae";

/**
 * Interface pour le composant PercentageField
 */
export interface PercentageFieldProps {
  formik: FormikProps<SilaeFormData>;
  fieldName: string;
  selectedVariable: SilaeVariable | null;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * Composant pour définir une opération mathématique sur une variable numérique
 */
export const PercentageField: React.FC<PercentageFieldProps> = ({
  formik,
  fieldName,
  selectedVariable,
  value,
  onChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const { errorClass } = useFormikFieldError(formik, fieldName);

  const valueInput = value || getIn(formik.values, fieldName) || "100";

  // S'assurer que le champ principal est initialisé dans Formik
  React.useEffect(() => {
    if (!getIn(formik.values, fieldName)) {
      formik.setFieldValue(fieldName, "100", false);
    }
  }, [formik, fieldName]);

  if (selectedVariable?.type !== "int" && selectedVariable?.type !== "double") {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Section 1: Pourcentage */}
      <div className="space-y-2">
        <Label>{t("percentageField.label")}</Label>
        <div className="flex gap-2 items-center">
          <p className="text-xs">{selectedVariable.code} × </p>
          <div className="w-28">
            <div className="relative">
              <Input
                type="number"
                step="1"
                min="0.01"
                max="100"
                value={valueInput}
                onChange={(e) => {
                  const val = e.target.value;
                  if (onChange) {
                    onChange(e);
                  } else {
                    formik.setFieldValue(fieldName, val);
                  }
                }}
                onBlur={onBlur ? onBlur : formik.handleBlur}
                placeholder={t("percentageField.placeholder")}
                className={cn("text-xs pr-8", errorClass as string)}
                name={fieldName}
              />
              <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                %
              </span>
            </div>
          </div>
        </div>

        <FormikErrorMessage
          formik={formik as unknown as FormikProps<Record<string, unknown>>}
          name={fieldName}
          hasError={parseInt(valueInput) > 100 || parseInt(valueInput) < 0}
        />

        {/* Aide contextuelle */}
        <div className="text-xs text-muted-foreground">
          <strong>{t("percentageField.help.title")}</strong>{" "}
          {t("percentageField.help.example")}
        </div>
      </div>
    </div>
  );
};
